<script setup lang="ts">
import {
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuRoot,
  NavigationMenuTrigger,
  NavigationMenuViewport
} from 'reka-ui'
import { NuxtLink } from '#components'
import type { NavigationMenu } from '~/types'

defineProps<{
  menus: NavigationMenu[]
  activeNav?: string
}>()

const currentTrigger = ref()

const isNextAreQuickLink = (menus: NavigationMenu, menuIndex: number) => {
  return menus.child?.[menuIndex + 1]?.quickLink === true
}
</script>

<template>
  <NavigationMenuRoot v-model="currentTrigger">
    <NavigationMenuList class="center m-0 flex list-none rounded-lg p-1">
      <NavigationMenuItem
        v-for="(nav, index) in menus"
        :key="index"
        :value="nav.title"
      >
        <NavigationMenuLink
          v-if="nav.to"
          class="select-none flex items-center px-3 py-4 h-12 text-sm font-medium leading-none no-underline outline-none"
          :class="{ 'text-primary': activeNav === nav.title }"
          :to="nav.to"
          :as="NuxtLink"
        >
          {{ nav.title }}
        </NavigationMenuLink>

        <template v-else>
          <NavigationMenuTrigger
            class="group flex select-none items-center justify-between gap-0.5 px-3 py-4 text-sm font-medium leading-none outline-none"
            :class="{ 'text-primary': activeNav === nav.title }"
          >
            {{ nav.title }}
            <Icon
              name="material-symbols:expand-more"
              size="16px"
              class="relative top-px transition-transform duration-150 ease-in group-data-[state=open]:-rotate-180"
            />
          </NavigationMenuTrigger>
          <NavigationMenuContent
            class="data-[motion=from-start]:animate-enterFromLeft data-[motion=from-end]:animate-enterFromRight data-[motion=to-start]:animate-exitToLeft data-[motion=to-end]:animate-exitToRight absolute top-0 left-0 w-full sm:w-auto"
          >
            <div
              class="list-none p-2 bg-surface-container-high/70 backdrop-blur-lg backdrop-saturate-150 rounded-3xl"
              :class="{
                'w-4xl': nav.child && nav.child.length > 2,
                'w-2xl': nav.child && nav.child.length === 2,
                'w-lg': nav.child && nav.child.length < 2
              }"
            >
              <div class="flex rounded-2xl">
                <div
                  v-for="(child, childIndex) in nav.child"
                  :key="childIndex"
                  :class="
                    cn(
                      'flex-1 p-2 flex flex-col first:not-[.quick]:rounded-l-2xl last:not-[.quick]:rounded-r-2xl',
                      {
                        'bg-background': !child.quickLink,
                        'rounded-r-2xl': isNextAreQuickLink(nav, childIndex),
                        quick: child.quickLink
                      }
                    )
                  "
                >
                  <p
                    v-if="child.group"
                    class="text-sm font-bold p-4"
                    :class="[
                      child.quickLink
                        ? 'text-on-surface-variant'
                        : 'text-outline'
                    ]"
                  >
                    {{ child.group }}
                  </p>
                  <NavbarMenuItem
                    v-for="(item, itemIndex) in child.items"
                    :key="`${child.group}-${itemIndex}`"
                    :title="item.title"
                    :to="item.to"
                    :icon="item.icon"
                    :description="item.description"
                    :quick-link="child.quickLink"
                    @click="currentTrigger = undefined"
                  />
                </div>
              </div>
            </div>
          </NavigationMenuContent>
        </template>
      </NavigationMenuItem>

      <NavigationMenuIndicator
        class="absolute data-[state=hidden]:opacity-0 duration-200 data-[state=visible]:animate-fadeIn data-[state=hidden]:animate-fadeOut top-full w-6 translate-x-[calc(24px+var(--reka-navigation-menu-indicator-position))] mt-px z-100 flex h-6 items-end justify-center overflow-hidden transition-[all,transform_250ms_ease] -translate-y-3.5"
      >
        <div
          class="relative top-[70%] h-6 w-6 aspect-square rotate-45 bg-surface-container-high/70"
        />
      </NavigationMenuIndicator>
    </NavigationMenuList>

    <div
      class="perspective-[2000px] absolute top-full z-90 left-0 flex flex-col w-full justify-center items-center"
    >
      <NavigationMenuViewport
        class="data-[state=open]:animate-scaleIn data-[state=closed]:animate-scaleOut relative mt-2.5 h-(--reka-navigation-menu-viewport-height) w-full origin-[top_center] overflow-hidden rounded-3xl transition-[width,height] duration-300 sm:w-(--reka-navigation-menu-viewport-width) shadow-sm"
      />
    </div>
  </NavigationMenuRoot>
</template>
